import { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import type { SavedChartState } from '../types';
import { exportChartToPDFVector } from '../utils/pdfExport';

export type PDFFormat = 'a4' | 'letter' | 'legal';
export type PDFOrientation = 'portrait' | 'landscape';

export interface PDFExportOptions {
  format: PDFFormat;
  orientation: PDFOrientation;
  measuresPerLine: number;
  linesPerPage: number;
  headerFontSize: number;  // Title, artist, style, key, time
  chordsFontSize: number;  // Chords in measures
  lyricsFontSize: number;  // Lyrics in measures
  includeChords: boolean;
  includeLyrics: boolean;
  includeAnnotations: boolean;
}

interface PDFExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (options: PDFExportOptions) => void;
  chart: SavedChartState;
}

export default function PDFExportModal({ isOpen, onClose, onExport, chart }: PDFExportModalProps) {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('');
  
  const [options, setOptions] = useState<PDFExportOptions>({
    format: 'a4',
    orientation: 'portrait',
    measuresPerLine: 4,
    linesPerPage: 8,
    headerFontSize: 12,
    chordsFontSize: 16,
    lyricsFontSize: 10,
    includeChords: true,
    includeLyrics: true,
    includeAnnotations: true,
  });

  // Revoke PDF blob URL on cleanup
  useEffect(() => {
    return () => {
      if (pdfBlobUrl.startsWith('blob:')) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Update preview when options change
  // Strategy: capture #printable-chart via html2canvas (always works, no CSP/iframe issues)
  //           + generate PDF blob URL for "Open PDF" link
  useEffect(() => {
    if (!isOpen) return;

    const updatePreview = async () => {
      setPreviewLoading(true);
      try {
        // 1. Generate PNG preview from the DOM (always visible, uses real fonts)
        const container = document.getElementById('printable-chart');
        const page = container?.firstElementChild as HTMLElement | null;
        if (page) {
          const canvas = await html2canvas(page, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
          });
          setPreviewUrl(canvas.toDataURL('image/png'));
        }

        // 2. Generate PDF blob URL for the "Open PDF" link
        const pdf = await exportChartToPDFVector(chart, options, true);
        if (pdf) {
          const arrayBuffer = pdf.output('arraybuffer') as ArrayBuffer;
          const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfBlobUrl(prev => {
            if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return url;
          });
        }
      } catch (error) {
        console.error('Preview generation failed:', error);
      } finally {
        setPreviewLoading(false);
      }
    };

    const timeout = setTimeout(updatePreview, 300);
    return () => clearTimeout(timeout);
  }, [isOpen, options, chart]);

  if (!isOpen) return null;

  const formatDimensions = {
    a4: { portrait: '8.3 × 11.7 in', landscape: '11.7 × 8.3 in' },
    letter: { portrait: '8.5 × 11 in', landscape: '11 × 8.5 in' },
    legal: { portrait: '8.5 × 14 in', landscape: '14 × 8.5 in' },
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Export PDF</h2>
            <p className="text-xs text-slate-400">{chart.songTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content: 2 columns layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Configuration (compact) */}
          <div className="w-80 border-r border-slate-700 overflow-y-auto p-4 space-y-4">
            {/* Format & Orientation */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Format</label>
              <select
                value={options.format}
                onChange={(e) => setOptions({ ...options, format: e.target.value as PDFFormat })}
                className="w-full bg-slate-700 text-white rounded px-2 py-1.5 text-xs border border-slate-600"
              >
                <option value="a4">A4 ({formatDimensions.a4[options.orientation]})</option>
                <option value="letter">Letter ({formatDimensions.letter[options.orientation]})</option>
                <option value="legal">Legal ({formatDimensions.legal[options.orientation]})</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Orientation</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setOptions({ ...options, orientation: 'portrait' })}
                  className={`px-2 py-1.5 rounded text-xs transition-all ${
                    options.orientation === 'portrait'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Portrait
                </button>
                <button
                  onClick={() => setOptions({ ...options, orientation: 'landscape' })}
                  className={`px-2 py-1.5 rounded text-xs transition-all ${
                    options.orientation === 'landscape'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  Landscape
                </button>
              </div>
            </div>

            {/* Layout */}
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Measures/Line: <span className="text-cyan-400 font-bold">{options.measuresPerLine}</span>
              </label>
              <input
                type="range"
                min="2"
                max="8"
                value={options.measuresPerLine}
                onChange={(e) => setOptions({ ...options, measuresPerLine: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1">
                Lines/Page: <span className="text-cyan-400 font-bold">{options.linesPerPage}</span>
              </label>
              <input
                type="range"
                min="4"
                max="12"
                value={options.linesPerPage}
                onChange={(e) => setOptions({ ...options, linesPerPage: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            {/* Typography */}
            <div className="border-t border-slate-700 pt-3 space-y-3">
              <label className="block text-xs font-semibold text-cyan-400 mb-2">Text Sizes</label>
              
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Header: <span className="text-cyan-400 font-bold">{options.headerFontSize}pt</span>
                </label>
                <input
                  type="range"
                  min="8"
                  max="18"
                  value={options.headerFontSize}
                  onChange={(e) => setOptions({ ...options, headerFontSize: parseInt(e.target.value) })}
                  className="w-full"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">Title, artist, key, time</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Chords: <span className="text-cyan-400 font-bold">{options.chordsFontSize}pt</span>
                </label>
                <input
                  type="range"
                  min="8"
                  max="24"
                  value={options.chordsFontSize}
                  onChange={(e) => setOptions({ ...options, chordsFontSize: parseInt(e.target.value) })}
                  className="w-full"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">Chords in measures</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Lyrics: <span className="text-cyan-400 font-bold">{options.lyricsFontSize}pt</span>
                </label>
                <input
                  type="range"
                  min="6"
                  max="16"
                  value={options.lyricsFontSize}
                  onChange={(e) => setOptions({ ...options, lyricsFontSize: parseInt(e.target.value) })}
                  className="w-full"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">Lyrics in measures</p>
              </div>
            </div>

            {/* Content Options */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Include</label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.includeChords}
                  onChange={(e) => setOptions({ ...options, includeChords: e.target.checked })}
                  className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                />
                <span className="text-xs text-slate-300">Chords</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.includeLyrics}
                  onChange={(e) => setOptions({ ...options, includeLyrics: e.target.checked })}
                  className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                />
                <span className="text-xs text-slate-300">Lyrics</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={options.includeAnnotations}
                  onChange={(e) => setOptions({ ...options, includeAnnotations: e.target.checked })}
                  className="w-4 h-4 rounded bg-slate-700 border-slate-600"
                />
                <span className="text-xs text-slate-300">Annotations</span>
              </label>
            </div>

            {/* Export Button */}
            <button
              onClick={() => onExport(options)}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold py-2 px-4 rounded-lg shadow-lg transition-all text-sm mt-6"
            >
              Export PDF
            </button>
          </div>

          {/* Right: Preview */}
          <div className="flex-1 bg-slate-900 p-4 overflow-auto flex items-start justify-center">
            <div className="relative w-full h-full flex flex-col">
              {previewLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-white text-sm">Generating preview...</div>
                </div>
              ) : previewUrl ? (
                <>
                  <img
                    src={previewUrl}
                    alt="Chart preview"
                    className="w-full border border-slate-700 shadow-xl bg-white"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-slate-400">Chart preview (export uses vector PDF)</p>
                    {pdfBlobUrl && (
                      <a
                        href={pdfBlobUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                      >
                        Open PDF ↗
                      </a>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-slate-400 text-sm">No preview available</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-800 border-t border-slate-700 px-4 py-2 flex gap-3 justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
